import { motion } from 'framer-motion';
import { Section, SectionHeader } from '@components/ui/Section.jsx';
import { usePageContent } from '@hooks/usePageContent.js';
import { HOME_DEFAULTS, ELITE_PARTNERS_DEFAULTS } from '@/content/homeDefaults.js';
import { assetUrl } from '@/lib/assetUrl.js';
import { cn } from '@/lib/cn.js';
import { OptimizedImage } from '@components/ui/OptimizedImage.jsx';

function PartnerLogo({ partner: p }) {
  const image = (
    <OptimizedImage
      src={assetUrl(p.logo)}
      alt={p.alt}
      className={cn(
        p.logoClassName ?? 'w-full max-h-24 sm:max-h-28 lg:max-h-32 object-contain',
        'transition-transform duration-500 ease-out-expo',
        !p.logoClassName && 'group-hover:scale-[1.04]',
      )}
      deferUntilVisible
    />
  );

  const framed = p.logoWrapClassName ? (
    <div className={cn('inline-flex items-center justify-center w-full h-full', p.logoWrapClassName)}>
      {image}
    </div>
  ) : (
    image
  );

  const linkText = p.linkText ? (
    <span className="mt-3 block text-center text-sm sm:text-base font-semibold tracking-wide text-brand-600 dark:text-brand-400 underline underline-offset-4 decoration-brand-400/70 group-hover:decoration-brand-500 group-hover:text-brand-700 dark:group-hover:text-brand-300 transition-colors">
      {p.linkText}
    </span>
  ) : null;

  if (!p.href) {
    return (
      <div className="inline-flex w-full h-full flex-col items-center justify-center">
        {framed}
        {linkText}
      </div>
    );
  }

  return (
    <a
      href={p.href}
      target="_blank"
      rel="noopener noreferrer"
      aria-label={p.alt || p.label}
      className="inline-flex w-full h-full flex-col items-center justify-center rounded-2xl focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-500/60 focus-visible:ring-offset-2"
    >
      {framed}
      {linkText}
    </a>
  );
}

export function PartnersSection() {
  const { content } = usePageContent('home', HOME_DEFAULTS);
  const section = content.elitePartners ?? ELITE_PARTNERS_DEFAULTS;
  const partners = section.partners ?? ELITE_PARTNERS_DEFAULTS.partners;

  return (
    <Section className="border-t border-ink-200/50 dark:border-ink-800/50 bg-ink-50/50 dark:bg-ink-900/20">
      <SectionHeader
        align="center"
        eyebrow={section.eyebrow}
        title={section.title}
        lead={section.lead}
      />

      <div className="mt-14 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-[1fr_1.55fr_1fr] gap-5 sm:gap-6 max-w-6xl mx-auto items-stretch">
        {partners.map((p, i) => (
          <motion.article
            key={`${p.label}-${i}`}
            initial={{ opacity: 0, y: 16 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, amount: 0.2 }}
            transition={{ delay: i * 0.06, duration: 0.5, ease: [0.16, 1, 0.3, 1] }}
            className={cn(
              'group flex flex-col h-full rounded-3xl p-4 sm:p-5 lg:p-6',
              'bg-gradient-to-b from-white via-white to-white/90 dark:from-ink-900/80 dark:via-ink-900/70 dark:to-ink-900/50 backdrop-blur-md',
              'border border-white/80 dark:border-ink-700/60',
              'shadow-[0_2px_32px_6px_rgba(255,255,255,0.75)] dark:shadow-soft hover:shadow-[0_4px_40px_8px_rgba(255,255,255,0.85)] dark:hover:shadow-glow hover:border-brand-200/80 dark:hover:border-brand-500/30',
              'hover:-translate-y-1 transition-all duration-300 ease-out-expo',
              p.wide && 'sm:col-span-2 lg:col-span-1',
              p.cardClassName,
            )}
          >
            <p className="text-xs sm:text-sm uppercase tracking-[0.14em] font-semibold text-ink-500 dark:text-ink-400 text-center leading-snug min-h-[2.25rem] flex items-center justify-center">
              {p.label}
            </p>

            <div
              className={cn(
                'mt-3 flex flex-1 items-center justify-center px-1',
                p.logoAreaClassName ?? 'min-h-[120px] sm:min-h-[132px] lg:min-h-[144px]',
              )}
            >
              <PartnerLogo partner={p} />
            </div>
          </motion.article>
        ))}
      </div>
    </Section>
  );
}
